import React, { useState, useEffect } from 'react'
import { Route } from 'react-router-dom'
import { isEmpty, values, map } from 'lodash'
import { useHistory } from 'react-router-dom'
import useDeepCompareEffect from 'use-deep-compare-effect'
import cn from 'classnames'
import generateUuid from 'uuid/v4'

import OrderInformation from './OrderInformation'
import CardReader from '../../CardReader'
import SidebarModal from '../../SidebarModal'
import Button from '../../Button'
import BackChevron from '../../BackChevron'
import TabOrderItems from '../TabOrderItems'
import TabOrderTotals from '../TabOrderTotals'
import RadioSelectionModal from '../../RadioSelectionModal'

import { calculateTaxAndTotal } from '../../../utils/exemptionUtils'
import PaymentTypes from '../../../utils/paymentTypes'
import useManagerApproval from '../../../hooks/useManagerApproval'

import styles from './Close.module.scss'
import { SplitOrderNumberManager } from '../../../utils/orderNumbers'
import { fakeCreditCardResponse } from '../../../utils/fakeCreditCardResponse'

const resolveTitle = (closeWithNewCard, splitOrderType) => {
  if (splitOrderType === 'tab') return "Start New Tab"
  if (closeWithNewCard) return "Close With New Card"

  return "Close Order"
}

const Close = ({
  authorizationFailed,
  cashEnabled,
  splittableAmount,
  amountDisplay,
  fractionalQuantity,
  className,
  deferClose,
  deviceReceiptPrinterUuid,
  discountsAvailable,
  exemptionOverrides,
  fetchPrinters,
  frozen,
  groupedItems,
  hideModifiedAt,
  hidePayment,
  isOrderLatestFromServer,
  isSplitOrder,
  isFractionalSplit,
  isRichCheckoutEnabled,
  itemCount,
  items,
  modifiedAt,
  noOpPayments,
  onCashPayment,
  onStoredCardPayment,
  onRemotePayment,
  onNewCardPayment,
  onNoOpPayment,
  onSelectNoOpPayment,
  onClose,
  onViewCheck,
  orderId,
  orderNumber,
  orderTotalFromOrder,
  payment,
  paymentId,
  unfilteredPayment,
  paymentOnOrder,
  paymentOptions,
  paymentType,
  peripheralStatus,
  printOrder,
  printers,
  splitOrderType,
  subtotal,
  suiteInformation,
  total,
  pendingMutation,
  splitTotal,
  onFinalize,
  userNotes,
  isMultiPayment,
}) => {
  const classNames = cn(styles.close, className)
  const history = useHistory()

  const simulatedOrderTotals = {
    totalsWithDiscount: orderTotalFromOrder,
    totalsWithDiscountNotApplied: orderTotalFromOrder
  }

  const orderLoading = !(pendingMutation === undefined)
  const [approvalModal, requestApproval] = useManagerApproval()
  const [closeWithNewCard, setCloseWithNewCard] = useState(false)
  const [exemptions, setExemptions] = useState(exemptionOverrides)

  const taxAndTotal = calculateTaxAndTotal(simulatedOrderTotals, exemptions)
  const [grandTotal, setGrandTotal] = useState(taxAndTotal.total)
  const [tax, setTax] = useState(taxAndTotal.tax)

  const [newOrderUuid] = useState(generateUuid()) // for split orders -- needed here for card reader
  const [switchPaymentFlag, setSwitchPaymentFlag] = useState(false)
  const [showPrinterSelection, setShowPrinterSelection] = useState(false)
  const [showConfirmCloseWithoutTip, setShowConfirmCloseWithoutTip] = useState(false)
  const [showNoOpPayments, setShowNoOpPayments] = useState(false)

  const splitOrderItems = isSplitOrder ? items : undefined
  let grandTotalElement = (
    <TabOrderTotals
      className={styles.tabOrderTotals}
      discount={orderTotalFromOrder?.discountDisplay}
      exemptions={exemptions}
      nonExemptableTaxRules={values(orderTotalFromOrder?.nonExemptableTaxRules ?? [])}
      serviceCharge={orderTotalFromOrder?.serviceChargeDisplay || '-'}
      serviceChargeName={orderTotalFromOrder?.serviceChargeName || 'Service Charge'}
      subtotal={orderTotalFromOrder?.subtotalDisplay}
      tax={orderTotalFromOrder?.taxDisplay}
      total={total}
    />
  )

  const updateExemptionsAndTotals = () => {
    setExemptions(exemptionOverrides)

    const taxAndTotal = calculateTaxAndTotal(simulatedOrderTotals, exemptionOverrides)
    setTax(taxAndTotal.tax)
    setGrandTotal(taxAndTotal.total)
  }

  const handlePrint = () => {
    if (!deviceReceiptPrinterUuid) {
      setShowPrinterSelection(!showPrinterSelection)
    } else {
      printOrder({
        printType: 'suite-receipt-auth',
        printerUuids: [deviceReceiptPrinterUuid],
        orderUuid: orderId,
        paymentUuid: paymentId,
      })
    }
  }

  // If ordering preferences or totals are changing, then the system hasn't reached a default
  // exemption consensus, so the exemptions should be overwritten with what is dictated by the
  // totals and preferences.
  useDeepCompareEffect(() => {
    updateExemptionsAndTotals()
  }, [simulatedOrderTotals, exemptionOverrides])

  useEffect(() => {
    if (items.length) {
      fetchPrinters()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length])

  const printerSelection = (
    <RadioSelectionModal
      show={showPrinterSelection}
      toggleShow={setShowPrinterSelection}
      items={printers}
      title={"Print Receipt"}
      selectionTitle={"Select a printer"}
      buttonText={"PRINT"}
      className={classNames}
      onDone={(printerSelected) =>
        printOrder({
          printType: "suite-receipt-auth",
          printerUuids: [printerSelected?.uuid],
          orderUuid: orderId,
          paymentUuid: paymentId,
        })
      }
    />
  )

  const makePaymentButton = (paymentPreference) => (
    <Button
      key={paymentPreference?.externalRefId}
      disabled={!isOrderLatestFromServer}
      onClick={() => {
        onRemotePayment(paymentPreference, exemptions, splitOrderItems, isFractionalSplit, fractionalQuantity, splittableAmount, frozen, deferClose)
      }}
      className={styles.actionButton}
    >
      {paymentPreference?.shortDescription}
    </Button>
  )

  const richCheckoutButton = isRichCheckoutEnabled && <Button key='mobile-wallet' onClick={() => history.push(`/rich-checkout/${orderId}`)} className={styles.actionButton} disabled={orderLoading || !isOrderLatestFromServer}>App</Button>
  
  let buttons = [
    discountsAvailable && <Button key='select-discount' className={styles.actionButton} onClick={() => history.replace(`/tabs/${orderId}/close/select-discount`)}>Discounts</Button>,
    !isMultiPayment && <Button key='split-payment' onClick={() => history.push(`/tender/${orderId}`)} className={styles.actionButton} disabled={orderLoading || !isOrderLatestFromServer}>Split Payment</Button>,
    cashEnabled && <Button key='cash' onClick={() => onCashPayment(exemptions, splitOrderItems, isFractionalSplit, fractionalQuantity, splittableAmount)} className={styles.actionButton} disabled={orderLoading || !isOrderLatestFromServer}>CASH</Button>,
    richCheckoutButton,
    <Button key='card' onClick={() => {setCloseWithNewCard(true)}} className={styles.actionButton} disabled={orderLoading ||  !isOrderLatestFromServer}>Card Reader</Button>,
    paymentOptions.length >= 1 && <Button key='switch payment' onClick={() => setSwitchPaymentFlag(true)} className={styles.actionButton} disabled={orderLoading}>Saved Payments</Button>,
    PaymentTypes.isCardReader(paymentType) && !isSplitOrder && <Button onClick={() => {
      if (authorizationFailed) {
        setShowConfirmCloseWithoutTip(true)
      } else {
        onStoredCardPayment(exemptions, frozen, authorizationFailed, deferClose)
        if (deferClose) {
          history.push(`/tabs/${orderId}/close/actions`)
        }
      }
    }} className={[styles.actionButton, styles.mintGreenHighlight]} disabled={orderLoading || !isOrderLatestFromServer}>{authorizationFailed ? unfilteredPayment : payment}</Button>,
    !isEmpty(noOpPayments) && <Button key='no-op select' onClick={() => onSelectNoOpPayment(deferClose)} className={styles.actionButton}>Other</Button>,
    !isEmpty(paymentOnOrder) && <Button key='no-op payment' onClick={() => {
      onNoOpPayment(paymentOnOrder, exemptions, deferClose )
    }} className={[styles.actionButton, styles.mintGreenHighlight]} disabled={orderLoading || !isOrderLatestFromServer}>{paymentOnOrder?.shortDescription}</Button>,
  ]

  if (splitOrderType === 'tab') {
    buttons = [
      <Button key='card' onClick={() => setCloseWithNewCard(true)} className={styles.actionButton} disabled={orderLoading || !isOrderLatestFromServer}>Card Reader</Button>,
      richCheckoutButton,
      paymentOptions.length >= 1 && <Button key='switch payment' onClick={() => setSwitchPaymentFlag(true)} className={styles.actionButton} disabled={orderLoading}>Saved Payments</Button>,
      PaymentTypes.isCardReader(paymentType) && !isSplitOrder && <Button onClick={() => {
        if (authorizationFailed) {
          setShowConfirmCloseWithoutTip(true)
        } else {
          onStoredCardPayment(exemptions, frozen, authorizationFailed, deferClose)
        }
      }} className={[styles.actionButton, styles.mintGreenHighlight]} disabled={orderLoading || !isOrderLatestFromServer}>{authorizationFailed ? unfilteredPayment : payment}</Button>,
      !isEmpty(paymentOnOrder) && <Button key='remote payment' onClick={() => {
        onRemotePayment(paymentOnOrder, exemptions, splitOrderItems, isFractionalSplit, fractionalQuantity, splittableAmount, frozen, deferClose)
      }} className={[styles.actionButton, styles.mintGreenHighlight]} disabled={orderLoading || !isOrderLatestFromServer}>{paymentOnOrder?.shortDescription}</Button>,
    ]
  }

  if (closeWithNewCard) {
    buttons = []
  }

  const isSplitByItem = splitOrderType !== undefined && !isFractionalSplit
  const isSplitFractionally = splitOrderType !== undefined && isFractionalSplit

  let orderInformationProps = {
    account: suiteInformation?.displayName,
    hidePayment: hidePayment,
    hideModifiedAt: hideModifiedAt,
    payment: payment,
    amount: isSplitOrder || frozen ? undefined : amountDisplay,
    modifiedAt: modifiedAt,
    grandTotal: splitTotal,
    orderNumber: isSplitOrder ? undefined : orderNumber,
    note: userNotes
  }

  if (isSplitByItem) {
    orderInformationProps.amount = subtotal
    orderInformationProps.grandTotal = undefined
  }

  if (isSplitFractionally) {
    orderInformationProps.amount = undefined
    orderInformationProps.grandTotal = splitTotal
  }

  let content = (
    <>
      <OrderInformation {...orderInformationProps} />
      {(splitOrderType !== 'tab' && !isFractionalSplit) && <div>
        {grandTotalElement}
      </div>}
      {splitOrderType === 'tab' && <TabOrderItems className={styles.itemsContainer} items={groupedItems} />}
    </>
  )

  if (closeWithNewCard) {
    content = (
      <>
        {isSplitOrder && <OrderInformation hidePayment hideModifiedAt={hideModifiedAt} account={suiteInformation?.displayName} amount={amountDisplay} modifiedAt={modifiedAt} />}
        <CardReader
          uuid={isSplitOrder ? newOrderUuid : orderId}
          total={total.replace('$', '')}
          onCancel={() => setCloseWithNewCard(false)}
          onSuccess={(cardReaderData) => {
            onNewCardPayment(cardReaderData, exemptions, splitOrderItems, newOrderUuid, isFractionalSplit, fractionalQuantity, splittableAmount, frozen, deferClose)
          }}
          onDone={onClose}
          authorizationType="auth"
          peripheralStatus={peripheralStatus}
          orderNumber={isSplitOrder ? SplitOrderNumberManager.findOrCreate(orderNumber) : orderNumber}
        />
      </>
    )
  }

  const leftAccessoryElement = (
    <>
      <Route exact path="/tabs/:orderId/close/actions">
        <BackChevron onClick={() => {
          history.replace(`/tabs/${orderId}/close`)
        }} />
      </Route>
      <Route exact path="/tabs/:orderId/close">
        <BackChevron onClick={() => {
          if (closeWithNewCard) {
            setCloseWithNewCard(false)
          } else {
            history.replace(`/tabs/${orderId}`)
          }
        }} />
      </Route>
    </>
  )

  let authorizationFailedMessage = null

  if (authorizationFailed && showConfirmCloseWithoutTip) {
    authorizationFailedMessage = (
      <div className={styles.authorizationFailedMessage}>
        {`Authorization failed when attempting to add gratuity with this payment. Proceed to close the order for the authorized amount of ${total}?`}
      </div>
    )
  }

  if (showConfirmCloseWithoutTip) {
    buttons = [
      <Button key='confirm-close-without-tip' onClick={() => onStoredCardPayment(exemptions, frozen, authorizationFailed)} className={cn(styles.actionButton, styles.mintGreenHighlight)} disabled={orderLoading || !isOrderLatestFromServer}>CONFIRM</Button>,
      <Button key='cancel-close-without-tip' onClick={() => setShowConfirmCloseWithoutTip(false)} className={styles.actionButton} disabled={orderLoading || !isOrderLatestFromServer}>CANCEL</Button>,
    ]
  }
  return (
    <SidebarModal
      show
      title={resolveTitle(closeWithNewCard, splitOrderType)}
      onClose={onClose}
      leftAccessory={leftAccessoryElement}
      className={classNames}
    >
      <div className={styles.bodyContainer}>
        <Route exact path="/tabs/:orderId/close/actions">
          <OrderInformation account={suiteInformation?.displayName} hidePayment={hidePayment} hideModifiedAt={hideModifiedAt} payment={payment} amount={amountDisplay} modifiedAt={modifiedAt} orderNumber={isSplitOrder ? undefined : orderNumber} />
          <div className={styles.buttonContainer}>
            <Button disabled={orderLoading} key={'Review & Sign Screen'} onClick={() => onViewCheck(paymentId)} className={styles.actionButton}>{'Review & Sign Screen'}</Button>
            <Button disabled={orderLoading} key={'Print Slip'} onClick={handlePrint} className={styles.actionButton}>{'Print Slip'}</Button>
            <Button disabled={orderLoading} key={'Finalize Later'} onClick={onFinalize} className={styles.actionButton}>{'Finalize'}</Button>
          </div>
          {showPrinterSelection && printerSelection}
        </Route>
        <Route exact path="/tabs/:orderId/close">
          {content}
          <div className={styles.buttonContainer}>
            {authorizationFailedMessage}
            {buttons}
          </div>
          {approvalModal}
        </Route>
      </div>
    </SidebarModal>
  )
}

export default Close
